import { useCallback, useEffect, useState } from "react"

import { api, type AlcanceDeConfiguracoes, type TipoDeEntidadeNoCadastro, type RespostaDosTiposDeEntidade } from "../api"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { ComDica } from "../componentes/ComDica"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Skeleton } from "../componentes/Skeleton"
import { toast } from "../componentes/Toast"
import * as Icones from "../componentes/Icones"
import { Input } from "../componentes/Input"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Acao, Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { inteiro } from "../formato"
import { BOTAO_BRANCO, BOTAO_CANCELAR, CABECALHO_DO_SHEET, MAIUSCULA, RECUSA } from "../lib/classes"
import { cn } from "../lib/cn"

type AbaDosTipos = "todos" | "ativos" | "inativos"

const ABAS_DOS_TIPOS: DescricaoDeAba<AbaDosTipos>[] = [
  { chave: "todos", rotulo: "Todos", dica: "Mostra todos os tipos de entidade" },
  { chave: "ativos", rotulo: "Ativos", dica: "Mostra só os tipos que aparecem na lista de escolha" },
  { chave: "inativos", rotulo: "Inativos", dica: "Mostra só os tipos desativados" },
]

const POR_PAGINA = 10

type Gaveta = { modo: "novo" } | { modo: "editar"; tipo: TipoDeEntidadeNoCadastro } | null

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase()

const paraMostrar = (nome: string) => nome.trim().replace(/\s+/g, " ").toLocaleUpperCase("pt-BR")

const entidadesQueContinuam = (quantas: number) =>
  quantas === 0
    ? "Nenhuma entidade usa este tipo."
    : quantas === 1
      ? "A entidade que o usa continua com ele."
      : `As ${inteiro(quantas)} entidades que o usam continuam com ele.`

export function TiposDeEntidade({
  alcance,
  aoVerOndeEUsado,
}: {
  alcance: AlcanceDeConfiguracoes
  aoVerOndeEUsado?: (nome: string) => void
}) {
  const [tipos, definirTipos] = useState<TipoDeEntidadeNoCadastro[] | null>(null)
  const [podeAlterar, definirPode] = useState(false)
  const [falha, definirFalha] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [aba, definirAba] = useState<AbaDosTipos>("todos")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA)
  const [gaveta, definirGaveta] = useState<Gaveta>(null)
  const [desativando, definirDesativando] = useState<TipoDeEntidadeNoCadastro | null>(null)

  const aplicar = useCallback((resposta: RespostaDosTiposDeEntidade) => {
    definirTipos(resposta.dados)
    definirPode(resposta.pode_alterar)
    definirFalha(null)
  }, [])

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .cadastroDeTiposDeEntidade()
      .then(aplicar)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [aplicar])

  useEffect(carregar, [carregar])

  if (tipos === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tipos de entidade</CardTitle>
        </CardHeader>
        {falha !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {falha}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={carregar}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const procurado = normalizar(busca)
  const filtrados = tipos.filter(
    (c) => (aba === "todos" || (aba === "ativos") === c.ativo) && (procurado === "" || normalizar(c.nome).includes(procurado)),
  )
  const totalDePaginas = Math.max(1, Math.ceil(filtrados.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = filtrados.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)

  const mudar = (tipo: TipoDeEntidadeNoCadastro, ativo: boolean) => {
    api
      .alterarTipoDeEntidade(tipo.id, { ativo })
      .then((resposta) => {
        aplicar(resposta)
        toast.success(`Tipo ${tipo.nome} ${ativo ? "reativado" : "desativado"}`)
      })
      .catch((erro: unknown) => toast.error(mensagemDeRecusa(erro)))
  }

  const abrirGaveta = (nova: Gaveta) => {
    definirGaveta(nova)
  }

  const vazio =
    tipos.length === 0 ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Etiqueta />
          </EmptyMedia>
          <EmptyTitle>Nenhum tipo de entidade cadastrado</EmptyTitle>
          <EmptyDescription>{podeAlterar ? "Cadastre o primeiro tipo, usado na classificação das entidades." : "Só o Root cadastra."}</EmptyDescription>
        </EmptyHeader>
        {podeAlterar && (
          <EmptyContent>
            <Button type="button" className="h-10" onClick={() => definirGaveta({ modo: "novo" })}>
              <Icones.Mais />
              Adicionar tipo
            </Button>
          </EmptyContent>
        )}
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nada encontrado</EmptyTitle>
          <EmptyDescription>{busca !== "" ? `Nada casa com "${busca}".` : "Nada nesta situação."}</EmptyDescription>
        </EmptyHeader>
        {busca !== "" && (
          <EmptyContent>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
              Limpar a busca
            </Button>
          </EmptyContent>
        )}
      </Empty>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Tipos de entidade{" "}
          <ComDica
            dica={
          podeAlterar
            ? "Os tipos valem para todas as entidades. Se desativado, sai da lista de escolha e continua nas entidades que já o usam."
            : alcance.conta_de_plataforma
              ? "Os tipos valem para todas as entidades, inclusive as reais, e esta é a conta de demonstração: ela os consulta, e só o Root os altera."
              : "Os tipos valem para todas as entidades, e só o Root os altera."
          }
          />
        </CardTitle>
      </CardHeader>


      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={filtrados.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          acaoNoTopo={
            podeAlterar && (
              <Button type="button" className="h-10" onClick={() => abrirGaveta({ modo: "novo" })}>
                <Icones.Mais />
                Adicionar tipo
              </Button>
            )
          }
          aoClicarNaLinha={podeAlterar ? (item) => definirGaveta({ modo: "editar", tipo: item }) : undefined}
          abasNaBarra={
            <Abas
              nome="tipos-de-entidade"
              rotulo="Situação dos tipos de entidade"
              classe="mb-0"
              abas={ABAS_DOS_TIPOS}
              atual={aba}
              aoTrocar={(chave) => {
                definirAba(chave)
                definirPagina(1)
              }}
            />
          }
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Nome" />
          <Column<TipoDeEntidadeNoCadastro> name="nome" label="Tipo" enableSorting={false} render={(valor) => <span className="font-medium">{String(valor)}</span>} />
          <Column<TipoDeEntidadeNoCadastro> name="entidades" label="Entidades" enableSorting={false} render={(valor) => inteiro(Number(valor))} />
          <Column<TipoDeEntidadeNoCadastro>
            name="ativo"
            label="Situação"
            enableSorting={false}
            render={(_valor, tipo) => <Selo situacao={tipo.ativo ? "ativa" : "inativa"}>{tipo.ativo ? "Ativo" : "Inativo"}</Selo>}
          />
          {podeAlterar && <Acao<TipoDeEntidadeNoCadastro> label="Editar" icon={Icones.Anotacao} onClick={(tipo) => abrirGaveta({ modo: "editar", tipo })} />}
          <Acao<TipoDeEntidadeNoCadastro>
            label="Ver onde é usado"
            icon={Icones.Buscar}
            visivel={(item) => item.entidades > 0}
            onClick={(item) => aoVerOndeEUsado?.(item.nome)}
          />
          {podeAlterar && <Acao<TipoDeEntidadeNoCadastro> label="Desativar" icon={Icones.Dispensar} visivel={(tipo) => tipo.ativo} onClick={definirDesativando} />}
          {podeAlterar && <Acao<TipoDeEntidadeNoCadastro> label="Reativar" icon={Icones.Reabrir} visivel={(tipo) => !tipo.ativo} onClick={(tipo) => mudar(tipo, true)} />}
        </TablePrincipal2>
      </CardContent>

      <AlertDialog open={desativando !== null} onOpenChange={(aberto) => !aberto && definirDesativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom="perigo">
            <AlertDialogTitle>Desativar o tipo {desativando?.nome}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Ele sai da lista de escolha. {entidadesQueContinuam(desativando?.entidades ?? 0)} É possível reativar depois.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (desativando !== null) mudar(desativando, false)
                  definirDesativando(null)
                }}
              >
                <Icones.Dispensar />
                Desativar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={gaveta !== null} onOpenChange={(aberta) => !aberta && definirGaveta(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">
              {gaveta?.modo === "editar" ? `Tipo de entidade: ${gaveta.tipo.nome}` : "Novo tipo de entidade"}
            </SheetTitle>
          </SheetHeader>
          {gaveta !== null && (
            <FormularioDoTipo
              key={gaveta.modo === "editar" ? gaveta.tipo.id : "novo"}
              inicial={gaveta.modo === "editar" ? gaveta.tipo : null}
              tipos={tipos}
              aoCancelar={() => definirGaveta(null)}
              aoSalvar={(resposta, nome) => {
                aplicar(resposta)
                definirGaveta(null)
                toast.success(`Tipo ${paraMostrar(nome)} salvo`)
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

function FormularioDoTipo({
  inicial,
  tipos,
  aoCancelar,
  aoSalvar,
}: {
  inicial: TipoDeEntidadeNoCadastro | null
  tipos: TipoDeEntidadeNoCadastro[]
  aoCancelar: () => void
  aoSalvar: (resposta: RespostaDosTiposDeEntidade, nome: string) => void
}) {
  const [nome, definirNome] = useState(inicial?.nome ?? "")
  const [enviando, definirEnviando] = useState(false)
  const [recusa, definirRecusa] = useState<string | null>(null)

  const repetido = tipos.find((c) => c.id !== inicial?.id && normalizar(nome) !== "" && normalizar(c.nome) === normalizar(nome))
  const longo = nome.trim().length > 45
  const falta = faltaNoFormulario([["o nome", nome]])
  const bloqueio = falta ?? (repetido ? `Já existe o tipo ${repetido.nome}` : longo ? "O nome passa de 45 caracteres" : null)

  const salvar = () => {
    definirEnviando(true)
    definirRecusa(null)
    const pedido = inicial === null ? api.criarTipoDeEntidade(nome) : api.alterarTipoDeEntidade(inicial.id, { nome })
    pedido
      .then((resposta) => aoSalvar(resposta, nome))
      .catch((erro: unknown) => definirRecusa(mensagemDeRecusa(erro)))
      .finally(() => definirEnviando(false))
  }

  return (
    <form
      className="flex flex-col gap-4 px-6 pt-3 pb-6"
      onSubmit={(evento) => {
        evento.preventDefault()
        if (bloqueio === null && !enviando) salvar()
      }}
    >
      <div>
        <RotuloDoCampo htmlFor="tipo-de-entidade-nome" obrigatorio>
          Nome
        </RotuloDoCampo>
        <Input
          id="tipo-de-entidade-nome"
          required
          autoComplete="off"
          maxLength={60}
          value={nome}
          aria-invalid={repetido !== undefined || longo || undefined}
          aria-describedby={repetido || longo ? "tipo-de-entidade-nome-aviso" : undefined}
          onChange={(evento) => definirNome(evento.target.value)}
        />
        {(repetido || longo) && (
          <p id="tipo-de-entidade-nome-aviso" className={cn(RECUSA, "mt-1.5")}>
            {repetido ? `Já existe o tipo ${repetido.nome}.` : "O nome passa de 45 caracteres."}
          </p>
        )}
      </div>
      {recusa !== null && (
        <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
          {recusa}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button type="submit" disabled={bloqueio !== null || enviando} title={bloqueio ?? undefined}>
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </form>
  )
}
